import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Beaker, Menu, X, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useThemeStore } from '@/lib/stores';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

/**
 * PublicLayout — Shared header/footer for ALL public pages.
 * One consistent nav, theme toggle, mobile menu.
 */
export default function PublicLayout() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [config, setConfig] = useState({ mode: 'community', show_pricing: false });
  const { theme, toggleTheme } = useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/public/config`).then(r => { if (r.data) setConfig(r.data); }).catch(() => {});
  }, []);

  const year = new Date().getFullYear();

  const links = [
    { to: '/features', label: 'Features' },
    { to: '/about', label: 'About' },
    { to: '/architecture', label: 'Architecture' },
    ...(config.show_whitepaper !== false ? [{ to: '/whitepaper', label: 'Whitepaper' }] : []),
    { to: '/blog', label: 'Blog' },
    ...(config.show_pricing ? [{ to: '/pricing', label: 'Pricing' }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Beta Banner */}
      {config.beta_banner && (
        <div className="bg-primary/10 border-b border-primary/20 text-center py-2 px-4">
          <span className="text-xs text-primary font-medium">{config.beta_text || 'Public Beta'}</span>
        </div>
      )}

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 h-14 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Beaker className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Pharmax.ai</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-5 text-sm text-muted-foreground">
            {links.map(l => (
              <Link key={l.to} to={l.to}
                className={`hover:text-foreground transition-colors ${isActive(l.to) ? 'text-foreground font-medium' : ''}`}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5">
            {/* Theme toggle */}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9 rounded-lg">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-sm hidden sm:inline-flex">
              Sign in
            </Button>
            <Button onClick={() => navigate('/signup')} className="rounded-full h-9 px-4 text-sm active:scale-[0.98]">
              Try App
            </Button>

            {/* Mobile hamburger */}
            <Button variant="ghost" size="icon" className="md:hidden h-9 w-9" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenu && (
          <div className="md:hidden absolute top-14 inset-x-0 bg-background border-b border-border p-4 space-y-1 z-50 shadow-lg">
            {links.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileMenu(false)}
                className={`block py-2.5 px-3 text-sm rounded-lg hover:bg-muted ${isActive(l.to) ? 'bg-muted font-medium' : ''}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/login" onClick={() => setMobileMenu(false)}
              className="block py-2.5 px-3 text-sm rounded-lg hover:bg-muted">Sign in</Link>
          </div>
        )}
      </nav>

      {/* Page content */}
      <main>
        <Outlet context={{ config, year }} />
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-4 sm:px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Beaker className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Pharmax.ai</span>
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-6 text-sm text-muted-foreground justify-center">
              {links.map(l => <Link key={l.to} to={l.to} className="hover:text-foreground">{l.label}</Link>)}
              <Link to="/join" className="hover:text-foreground">Join</Link>
            </div>
            <p className="text-xs text-muted-foreground">&copy; {year} Pharmax.ai</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
